// src/components/CompteStripe.jsx
// Carte "Compte de paiement" — à afficher en haut de MaBilletterie.
// Affiche l'état Stripe du créateur et le bouton pour activer / compléter.

import React, { useEffect, useState } from "react";
import { Wallet, CheckCircle2, Clock, AlertTriangle, ExternalLink, ShieldCheck, Landmark } from "lucide-react";
import { getStatutStripe, demarrerOnboardingStripe, ouvrirDashboardStripe } from "../services/StripeConnectService";

const ETATS = {
    non_connecte: {
        titre: "Activez vos paiements",
        texte: "Pour vendre des billets sur Thrundrz, connectez votre compte bancaire. Stripe, notre prestataire de paiement agréé, vous demandera vos informations en toute sécurité. Thrundrz ne voit jamais votre RIB.",
        bouton: "Activer mes paiements",
        icone: Wallet,
        couleur: "bg-violet-50 text-violet-600",
    },
    incomplet: {
        titre: "Configuration inachevée",
        texte: "Il manque encore quelques informations pour activer la vente de billets.",
        bouton: "Reprendre la configuration",
        icone: Clock,
        couleur: "bg-amber-50 text-amber-600",
    },
    en_verification: {
        titre: "Vérification en cours",
        texte: "Stripe vérifie vos informations. C'est généralement rapide. Vous pourrez vendre dès que c'est validé.",
        bouton: null,
        icone: Clock,
        couleur: "bg-blue-50 text-blue-600",
    },
    action_requise: {
        titre: "Action requise",
        texte: "Stripe a besoin d'informations supplémentaires. La vente de billets est suspendue en attendant.",
        bouton: "Compléter mes informations",
        icone: AlertTriangle,
        couleur: "bg-red-50 text-red-600",
    },
    actif: {
        titre: "Paiements activés",
        texte: "Vous pouvez vendre des billets. Vos revenus sont versés sur votre compte bancaire 15 jours après chaque événement.",
        bouton: null,
        icone: CheckCircle2,
        couleur: "bg-emerald-50 text-emerald-600",
    },
};

const CompteStripe = ({ retour = "/EspaceClient/billetterie", compact = false }) => {
    const [infos, setInfos] = useState(null);
    const [loading, setLoading] = useState(true);
    const [redirection, setRedirection] = useState(false);
    const [erreur, setErreur] = useState(null);

    const charger = async () => {
        setLoading(true);
        try {
            setInfos(await getStatutStripe());
            setErreur(null);
        } catch (e) {
            setErreur(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { charger(); }, []);

    const activer = async () => {
        setRedirection(true);
        try {
            await demarrerOnboardingStripe(retour);
        } catch (e) {
            setErreur(e.message);
            setRedirection(false);
        }
    };

    if (loading) return <div className="h-28 rounded-2xl bg-gray-100 animate-pulse mb-6" />;

    const etat = ETATS[infos?.statut] || ETATS.non_connecte;
    const Icone = etat.icone;

    return (
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${etat.couleur}`}>
                    <Icone size={20} />
                </div>

                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-gray-900">{etat.titre}</h3>
                    {!compact && <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{etat.texte}</p>}

                    {/* Infos récupérées depuis Stripe */}
                    {infos?.statut === "actif" && (
                        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-gray-600">
                            {infos.nom_affiche && <span className="font-semibold">{infos.nom_affiche}</span>}
                            {infos.banque && (
                                <span className="flex items-center gap-1">
                                    <Landmark size={12} className="text-gray-400" />
                                    {infos.banque.nom || "Compte bancaire"} •••• {infos.banque.last4}
                                </span>
                            )}
                            {infos.email && <span>{infos.email}</span>}
                        </div>
                    )}

                    {infos?.statut === "action_requise" && infos.requirements?.length > 0 && (
                        <p className="text-[11px] text-red-500 mt-1">
                            {infos.requirements.length} information{infos.requirements.length > 1 ? "s" : ""} manquante{infos.requirements.length > 1 ? "s" : ""}
                        </p>
                    )}
                    {erreur && <p className="text-[11px] text-red-500 mt-1">{erreur}</p>}
                </div>

                <div className="flex gap-2 shrink-0">
                    {etat.bouton && (
                        <button
                            onClick={activer}
                            disabled={redirection}
                            data-testid="btn-activer-stripe"
                            className="px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold transition disabled:opacity-60"
                        >
                            {redirection ? "Redirection…" : etat.bouton}
                        </button>
                    )}
                    {infos?.details_submitted && (
                        <button
                            onClick={() => ouvrirDashboardStripe().catch((e) => setErreur(e.message))}
                            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-semibold transition"
                        >
                            Mes versements <ExternalLink size={13} />
                        </button>
                    )}
                    {infos?.statut === "en_verification" && (
                        <button onClick={charger} className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-xs font-semibold">
                            ↻ Actualiser
                        </button>
                    )}
                </div>
            </div>

            {infos?.statut === "non_connecte" && !compact && (
                <p className="flex items-center gap-1.5 mt-3 pt-3 border-t border-gray-50 text-[11px] text-gray-400">
                    <ShieldCheck size={12} /> Paiements sécurisés par Stripe
                </p>
            )}
        </section>
    );
};

export default CompteStripe;